import { EnumContainerOrderAction } from '@Model';

/**
 * Where a container position came from. It decides both what the summary says about the position
 * and whether the dispatcher has to look at it before the tour.
 */
export type ContainerPositionOrigin = 'stock' | 'described' | 'placement';

/** The smallest number of containers one position may ask for. */
export const MIN_COUNT = 1;

/** Maximum count of a position whose container type is kept as a pool. */
export const MAX_COUNT_COLLECTIVE = 10;

/**
 * One container an order is about.
 *
 * An order is one intention: one container, or on an exchange the one that goes and the one that
 * comes. It is the wizard's view of a container action of the requisition, content included.
 */
export interface ContainerPosition {
    origin: ContainerPositionOrigin;
    /** What the row does with the container: collects, empties, places. */
    action: EnumContainerOrderAction;
    containerTypeId: string;
    containerTypeLabel: string;
    /** Whether the type is tracked per container. Undefined while its record has not arrived. */
    usesIndividualManagement: boolean | undefined;
    /** The container this position picked out of the stock; unset for every other origin. */
    containerId: string | undefined;
    /** Its number, either from the stock line or as the customer typed it. */
    containerNumber: string;
    /** Where it stands, as the customer describes it. */
    locationHint: string;
    count: number;
    /** How many containers of this line stand at the site or are planned for it; unset unless picked from the stock. */
    stockCount: number | undefined;
    /** The content, which carries pricing and waste code. Unset until the customer picks one. */
    serviceId: string | undefined;
    serviceLabel: string;
}

/** A position with nothing filled in, which is what an unanswered form shows. */
export function emptyPosition(origin: ContainerPositionOrigin): ContainerPosition {
    return {
        origin,
        action: origin === 'placement' ? EnumContainerOrderAction.Place : EnumContainerOrderAction.NotDefined,
        containerTypeId: '',
        containerTypeLabel: '',
        usesIndividualManagement: undefined,
        containerId: undefined,
        containerNumber: '',
        locationHint: '',
        count: MIN_COUNT,
        stockCount: undefined,
        serviceId: undefined,
        serviceLabel: '',
    };
}

/**
 * The highest count a position may carry.
 *
 * The limit follows the container kind, not the action: an individually managed type means one
 * container — the container *is* the amount — while a pool can be asked for up to ten at a time.
 */
export function maxCountFor(position: Pick<ContainerPosition, 'usesIndividualManagement'>): number {
    return position.usesIndividualManagement === false ? MAX_COUNT_COLLECTIVE : MIN_COUNT;
}

/**
 * Whether the dispatcher has to check this position before the tour.
 *
 * Two cases reach them: a container the customer described because it is not in our records, and
 * more containers picked from the stock than we have recorded at the site. A placement brings new
 * containers, so the stock says nothing about it.
 */
export function needsReview(position: ContainerPosition): boolean {
    if (position.origin === 'described') {
        return true;
    }

    return position.origin === 'stock' && position.stockCount !== undefined && position.count > position.stockCount;
}

/** Whether the content of a position is filled in, which it has to be before the wizard advances. */
export const hasContent = (position: ContainerPosition): boolean => !!position.serviceId;

/**
 * How the position reads in the summary and on a confirmed line.
 *
 * The type leads, because that is what the customer recognises; a named container adds its
 * number behind it, an unnamed one its count in front.
 */
export const positionTitle = (position: ContainerPosition): string =>
    position.containerNumber
        ? `${position.containerTypeLabel} · Nr. ${position.containerNumber}`
        : `${position.count} × ${position.containerTypeLabel}`;

/** Where the position came from, as the summary notes it beside the line. */
export function originNote(position: ContainerPosition): string {
    if (position.origin !== 'stock') {
        return '';
    }

    return position.usesIndividualManagement ? 'aus Bestand' : 'nummernlos · aus Bestand';
}

/** Why this position is going to the dispatcher, spelled out for the summary. */
export function reviewReason(position: ContainerPosition): string {
    if (position.origin === 'described') {
        const parts = ['Nicht in unserem Bestand'];
        if (position.containerNumber) {
            parts.push(`Nummer laut dir: ${position.containerNumber}`);
        }
        if (position.locationHint) {
            parts.push(`„${position.locationHint}“`);
        }
        return parts.join(' · ');
    }

    return `Bei uns sind hier nur ${position.stockCount ?? 0} erfasst`;
}
